import React, { useMemo } from 'react'
import styled from 'styled-components'
import messageUtil from 'utils/messageUtil'
import Sentiment, { SentimentStrings } from 'enums/Sentiment'
import { InsightData } from 'schemas/action/InsightDataSchema'
import { useDashboardWidgetData } from '../../../../../hooks/useWidgetState'
import { useDashboardWidgetSetting } from '../../../../../hooks/useDashboardState'
import DonutChart from '../../../../visualization/Donut'
import { getFilteredInsightDataSet } from '../utils'
import { InsightCount } from '../../../../../schemas/action/InsightCountSchema'
import { DonutSelectionType, SentimentWidgetsType } from '../../types'

/*
 * NI-2503 — original donut size + uncloak every Highcharts clip layer.
 *
 * History on this widget:
 *   - PR #92 shipped 120 px of bottom padding so outer data labels
 *     would fit inside `overflow: hidden`. The labels fit, but the
 *     donut visibly shrank because the wrapper padding ate into the
 *     chart's plot area. Customer rejected — "donut too small".
 *   - PR #94 dropped padding to 32 px and added panel
 *     `overflow: visible`. Better, but the donut was still smaller
 *     than the customer wanted and the south-pole label of a
 *     dominant slice still sat near the panel border on shorter
 *     cells.
 *
 * Source widget (also Highcharts donut) went through the same loop
 * and landed on the structural fix below (PR #95 commit 9bb1fd33).
 * Mirroring it here on the Sentiment Donut for layout consistency
 * and to remove the last bit of padding-induced shrinkage.
 *
 * The fix needs to defeat THREE clipping layers Highcharts puts
 * between the data label and the visible page:
 *   1. The HTML div wrapper (`.highcharts-container`) — `overflow`
 *   2. The root SVG (`.highcharts-root` / `<svg>`) — `overflow`
 *   3. Highcharts' own `<clipPath>` applied via `clip-path: url(#…)`
 *      on `<g>` nodes like `.highcharts-data-labels` — needs
 *      explicit `clip-path: none`, since `overflow: visible` does
 *      NOT override a CSS `clip-path`.
 * The wildcard `& *` belt-and-suspenders catches any descendant we
 * missed (e.g. future Highcharts internal name changes).
 */
export const SentimentDonutContainer = styled.div`
  width: 100%;
  height: 100%;

  & * {
    overflow: visible !important;
  }

  & .highcharts-data-labels,
  & .highcharts-data-label,
  & .highcharts-data-label-connector {
    clip-path: none !important;
    clip: auto !important;
  }
`

interface SentimentWidgetProps {
  widgetId: number
  seriesName: string
  onClick: (insightData: InsightData) => void
}

const SentimentWidget = ({
  widgetId,
  seriesName,
  onClick
}: SentimentWidgetProps): React.ReactElement => {
  const widgetSetting = useDashboardWidgetSetting<SentimentWidgetsType>(widgetId)
  const widgetData = useDashboardWidgetData<InsightCount>(widgetId)

  const colorSettings = useMemo(
    () => ({
      [messageUtil.sentiments(Sentiment.POSITIVE)]: 'semantic.positive',
      [messageUtil.sentiments(Sentiment.NEGATIVE)]: 'semantic.negative',
      [messageUtil.sentiments(Sentiment.NEUTRAL)]: 'semantic.neutral'
    }),
    []
  )

  // TODO: Log each go on widget (migration from V3)
  const filteredData = getFilteredInsightDataSet(widgetData, widgetSetting)

  const donutData = filteredData.map((dataEntry) => ({
    y: dataEntry.value,
    name: messageUtil.sentiments(dataEntry.name! as SentimentStrings)
  }))

  const findFilteredDataFromDonut = (donutItem: DonutSelectionType) => {
    const sentimentMap: Record<string, SentimentStrings> = {
      [messageUtil.sentiments(Sentiment.POSITIVE)]: Sentiment.POSITIVE,
      [messageUtil.sentiments(Sentiment.NEGATIVE)]: Sentiment.NEGATIVE,
      [messageUtil.sentiments(Sentiment.NEUTRAL)]: Sentiment.NEUTRAL
    }
    const sentimentName = sentimentMap[donutItem.seriesName]
    return filteredData.find((item) => item.name === sentimentName)
  }

  const handleClick = (donutItem: DonutSelectionType) => {
    const insightData = findFilteredDataFromDonut(donutItem)
    onClick?.(insightData!)
  }

  return (
    <SentimentDonutContainer>
      <DonutChart
        data={donutData}
        colorSettings={colorSettings}
        seriesName={seriesName}
        onClick={handleClick}
      />
    </SentimentDonutContainer>
  )
}

export default SentimentWidget
